import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Check, Plus, UserPlus, X } from 'lucide-react';
import {
  colorForAssignee,
  initialsFor,
  normalizeAssigneeKey,
  preferredAssigneeLabel,
} from '@/lib/assigneeDisplay';
import { useNotifications } from '@/contexts/NotificationsContext';
import type { AssigneeSuggestion } from '@/lib/api';

interface AssigneeTokensProps {
  value: string[];
  onChange: (next: string[]) => void;
  readOnly?: boolean;
  placeholder?: string;
  /** Внутри Sheet нужен портал в тот же контейнер, иначе блокируется прокрутка. */
  popoverPortalContainer?: HTMLElement | null;
}

function dedupe(list: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of list) {
    const t = v.trim();
    if (!t) continue;
    const k = normalizeAssigneeKey(t);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

export function AssigneeTokens({
  value,
  onChange,
  readOnly = false,
  placeholder = 'Добавить исполнителя',
  popoverPortalContainer,
}: AssigneeTokensProps) {
  const { assigneeMatches, fetchSuggestions } = useNotifications();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<AssigneeSuggestion[]>([]);
  const debounceRef = useRef<number | null>(null);

  const tokens = useMemo(() => dedupe(value), [value]);

  const addToken = useCallback(
    (raw: string) => {
      const t = raw.trim();
      if (!t) return;
      const k = normalizeAssigneeKey(t);
      if (tokens.some((x) => normalizeAssigneeKey(x) === k)) return;
      onChange([...tokens, t]);
      setQuery('');
    },
    [tokens, onChange],
  );

  const removeToken = useCallback(
    (token: string) => {
      const k = normalizeAssigneeKey(token);
      onChange(tokens.filter((x) => normalizeAssigneeKey(x) !== k));
    },
    [tokens, onChange],
  );

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      fetchSuggestions(query.trim())
        .then((list) => setItems(list))
        .catch(() => setItems([]));
    }, 150);
    return () => {
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
    };
  }, [open, query, fetchSuggestions]);

  // Initial open: показать первую партию.
  useEffect(() => {
    if (!open) return;
    fetchSuggestions('')
      .then((list) => setItems(list))
      .catch(() => setItems([]));
  }, [open, fetchSuggestions]);

  const selectedKeys = useMemo(
    () => new Set(tokens.map((x) => normalizeAssigneeKey(x))),
    [tokens],
  );

  const showAddRawButton =
    query.trim().length > 0 &&
    !selectedKeys.has(normalizeAssigneeKey(query)) &&
    !items.some(
      (it) =>
        normalizeAssigneeKey(it.login) === normalizeAssigneeKey(query) ||
        (it.displayName && normalizeAssigneeKey(it.displayName) === normalizeAssigneeKey(query)),
    );

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5">
      {tokens.map((raw) => {
        const match = assigneeMatches.get(normalizeAssigneeKey(raw)) ?? null;
        const label = preferredAssigneeLabel(raw, match);
        const bg = match ? colorForAssignee(normalizeAssigneeKey(match.login)) : undefined;
        const tooltip = match
          ? match.isRegistered
            ? `${match.login} — зарегистрирован, уведомление придёт`
            : `${match.login} — приглашён, но ещё не зарегистрировался`
          : 'Не найден среди пользователей — уведомление не будет отправлено';
        return (
          <Tooltip key={normalizeAssigneeKey(raw)} delayDuration={200}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded-full border px-1 py-0.5 text-[11px] font-medium leading-none',
                  match
                    ? 'border-transparent text-white'
                    : 'border-dashed border-border bg-muted/40 text-muted-foreground',
                )}
                style={match ? { backgroundColor: bg } : undefined}
              >
                <span
                  aria-hidden
                  className={cn(
                    'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold',
                    match ? 'bg-white/25 text-white' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {initialsFor(match?.displayName || match?.login || raw)}
                </span>
                <span className="max-w-[14ch] truncate">{label}</span>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeToken(raw);
                    }}
                    className={cn(
                      'ml-0.5 inline-flex size-3.5 shrink-0 items-center justify-center rounded-full transition-colors',
                      match ? 'hover:bg-white/25' : 'hover:bg-red-100 hover:text-red-600',
                    )}
                    aria-label={`Убрать ${label}`}
                  >
                    <X size={10} strokeWidth={2.5} />
                  </button>
                ) : null}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[22rem] text-xs">
              <div className="space-y-0.5">
                <div className="font-semibold">{label}</div>
                <div className="text-muted-foreground">{tooltip}</div>
                {match?.login && match.login !== label ? (
                  <div className="text-muted-foreground">{match.login}</div>
                ) : null}
              </div>
            </TooltipContent>
          </Tooltip>
        );
      })}

      {!readOnly ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[var(--nn-blue)] hover:bg-[var(--nn-blue-pale)]/40 hover:text-[var(--nn-blue)]"
            >
              <Plus size={11} strokeWidth={2.5} />
              {tokens.length === 0 ? placeholder : 'Добавить'}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={6}
            container={popoverPortalContainer}
            className="z-[100] w-[min(24rem,calc(100vw-1.5rem))] p-0 shadow-lg"
          >
            <Command shouldFilter={false} className="h-auto max-h-[min(22rem,calc(100dvh-9rem))] min-h-0 flex-col rounded-md">
              <CommandInput
                placeholder="Найти по ФИО или e-mail…"
                value={query}
                onValueChange={setQuery}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && query.trim()) {
                    addToken(query);
                    e.preventDefault();
                  }
                }}
              />
              <CommandList className="max-h-[min(18rem,calc(100dvh-11rem))] min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
                {items.length === 0 && !showAddRawButton ? (
                  <CommandEmpty>Никого не найдено</CommandEmpty>
                ) : null}
                {items.length > 0 ? (
                  <CommandGroup heading="Пользователи и приглашения">
                    {items.map((it) => {
                      const key = normalizeAssigneeKey(it.login);
                      const selected = selectedKeys.has(key);
                      const label = it.displayName?.trim() || it.login;
                      return (
                        <CommandItem
                          key={key}
                          value={`${it.login} ${it.displayName ?? ''}`}
                          onSelect={() => {
                            addToken(it.displayName?.trim() || it.login);
                            setOpen(false);
                          }}
                          className="cursor-pointer items-center gap-2 py-2"
                        >
                          <span
                            aria-hidden
                            className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
                            style={{ backgroundColor: colorForAssignee(key) }}
                          >
                            {initialsFor(label)}
                          </span>
                          <span className="min-w-0 flex-1 leading-tight">
                            <span className="block truncate text-sm">{label}</span>
                            <span className="block truncate text-[11px] text-muted-foreground">
                              {it.login}
                              {' · '}
                              {it.isRegistered ? (
                                <span className="text-emerald-600">зарегистрирован</span>
                              ) : (
                                <span className="text-amber-600">приглашён</span>
                              )}
                            </span>
                          </span>
                          {selected ? <Check size={14} className="text-muted-foreground" /> : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : null}
                {showAddRawButton ? (
                  <CommandGroup heading="Добавить произвольно">
                    <CommandItem
                      value={`__raw__${query}`}
                      onSelect={() => {
                        addToken(query);
                        setOpen(false);
                      }}
                      className="cursor-pointer items-center gap-2 py-2"
                    >
                      <UserPlus size={14} className="text-muted-foreground" />
                      <span className="min-w-0 flex-1 leading-tight">
                        <span className="block truncate text-sm">«{query.trim()}»</span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          без связи с пользователем — уведомление не уйдёт
                        </span>
                      </span>
                    </CommandItem>
                  </CommandGroup>
                ) : null}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
